import React, { useCallback, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ApexCharts from "apexcharts";
import { useIsSalesUser } from "../../../utils/salesUserScope";

const TEMPLATE_FONT_LINK_ID = "template-inter-font";

const MIN_ZOOM_SPAN = 2;
const ZOOM_IN_FACTOR = 0.7;

type ZoomableChart = {
  w: { globals: { minX: number; maxX: number; initialMinX: number; initialMaxX: number } };
  zoomX: (start: number, end: number) => void;
};

export const useWheelZoom = (chartId: string) => {
  const cleanupRef = useRef<(() => void) | null>(null);

  return useCallback(
    (node: HTMLDivElement | null) => {
      cleanupRef.current?.();
      cleanupRef.current = null;
      if (!node) return;

      const handleWheel = (event: WheelEvent) => {
        if (event.deltaY === 0) return;
        const chart = ApexCharts.getChartByID(chartId) as unknown as ZoomableChart | undefined;
        const globals = chart?.w?.globals;
        if (!chart || !globals) return;

        const fullMin = globals.initialMinX;
        const fullMax = globals.initialMaxX;
        const fullSpan = fullMax - fullMin;
        const span = globals.maxX - globals.minX;
        if (!(fullSpan > MIN_ZOOM_SPAN) || !(span > 0)) return;

        const zoomingIn = event.deltaY < 0;
        if (!zoomingIn && span >= fullSpan) return;
        event.preventDefault();

        const scaled = zoomingIn ? span * ZOOM_IN_FACTOR : span / ZOOM_IN_FACTOR;
        const nextSpan = Math.min(fullSpan, Math.max(MIN_ZOOM_SPAN, Math.round(scaled)));
        if (nextSpan === span) return;
        if (nextSpan >= fullSpan) {
          chart.zoomX(fullMin, fullMax);
          return;
        }

        const grid = node.querySelector(".apexcharts-grid")?.getBoundingClientRect();
        const ratio =
          grid && grid.width > 0 ? Math.min(1, Math.max(0, (event.clientX - grid.left) / grid.width)) : 0.5;
        const anchor = globals.minX + ratio * span;
        const nextMin = Math.min(Math.max(Math.round(anchor - ratio * nextSpan), fullMin), fullMax - nextSpan);
        chart.zoomX(nextMin, nextMin + nextSpan);
      };

      node.addEventListener("wheel", handleWheel, { passive: false });
      cleanupRef.current = () => node.removeEventListener("wheel", handleWheel);
    },
    [chartId]
  );
};

export const TEMPLATE_FONT_FAMILY = "Inter, sans-serif";

export const useTemplateFont = () => {
  useEffect(() => {
    if (document.getElementById(TEMPLATE_FONT_LINK_ID)) return;
    const link = document.createElement("link");
    link.id = TEMPLATE_FONT_LINK_ID;
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);
};

export const HeaderAction = ({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon: string;
  onClick: () => void;
}) => (
  <OverlayTrigger placement="top" overlay={<Tooltip id={`nd-${label}`}>{label}</Tooltip>}>
    <button
      type="button"
      className="btn btn-icon nd-header-btn shadow-sm"
      aria-label={label}
      onClick={onClick}
    >
      <i className={icon} />
    </button>
  </OverlayTrigger>
);

export const FilterDropdown = <T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: T[];
  onChange: (v: T) => void;
}) => (
  <div className="dropdown">
    <Link to="#" className="dropdown-toggle btn btn-outline-light shadow" data-bs-toggle="dropdown">
      {value}
    </Link>
    <div className="dropdown-menu dropdown-menu-end">
      {options.map((option) => (
        <Link
          key={option}
          to="#"
          className="dropdown-item"
          onClick={(e) => {
            e.preventDefault();
            onChange(option);
          }}
        >
          {option}
        </Link>
      ))}
    </div>
  </div>
);

export interface DropdownOption<T extends string> {
  value: T;
  label: string;
}

export const OptionDropdown = <T extends string>({
  value,
  options,
  onChange,
  disabled = false,
}: {
  value: T;
  options: DropdownOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
}) => (
  <div className="dropdown">
    <Link
      to="#"
      className={`dropdown-toggle btn btn-outline-light shadow${disabled ? " disabled" : ""}`}
      data-bs-toggle="dropdown"
    >
      {options.find((option) => option.value === value)?.label ?? value}
    </Link>
    <div className="dropdown-menu dropdown-menu-end">
      {options.map((option) => (
        <Link
          key={option.value}
          to="#"
          className={`dropdown-item${option.value === value ? " active" : ""}`}
          onClick={(event) => {
            event.preventDefault();
            onChange(option.value);
          }}
        >
          {option.label}
        </Link>
      ))}
    </div>
  </div>
);

export type ExportKind = "pdf" | "excel";

const EXPORT_ITEMS: { kind: ExportKind; icon: string; label: string }[] = [
  { kind: "pdf", icon: "ti ti-file-type-pdf", label: "Export as PDF" },
  { kind: "excel", icon: "ti ti-file-type-xls", label: "Export as Excel" },
];

export const ExportDropdown = ({
  disabled,
  exporting,
  spinClassName,
  onExport,
  kinds = ["pdf", "excel"],
}: {
  disabled: boolean;
  exporting: ExportKind | null;
  spinClassName: string;
  onExport: (kind: ExportKind) => void;
  kinds?: ExportKind[];
}) => {
  const isSalesUser = useIsSalesUser();
  if (isSalesUser) return null;
  return (
    <div className="dropdown">
      <Link
        to="#"
        className={`dropdown-toggle btn btn-outline-light px-2 shadow${disabled || exporting ? " disabled" : ""}`}
        data-bs-toggle="dropdown"
      >
        <i className={`ti ${exporting ? `ti-loader-2 ${spinClassName}` : "ti-package-export"} me-2`} />
        {exporting ? "Exporting..." : "Export"}
      </Link>
      <div className="dropdown-menu dropdown-menu-end">
        <ul>
          {EXPORT_ITEMS.filter((item) => kinds.includes(item.kind)).map((item) => (
            <li key={item.kind}>
              <Link
                to="#"
                className="dropdown-item"
                onClick={(e) => {
                  e.preventDefault();
                  onExport(item.kind);
                }}
              >
                <i className={`${item.icon} me-1`} />
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
